/**
 * The command palette — Ctrl/Cmd+K.
 *
 * Backed by **`POST /api/search` with the term in the body** (§9). Since all
 * tiers read everything (§2), it needs no scope filter — but the server still
 * filters `deleted_at is null` on every joined table.
 *
 * **A bare opp number jumps straight to that opportunity.** People paste them
 * out of the digest email, which carries identifiers and nothing else; making
 * them retype or click through a one-row result list would be a small insult
 * repeated several times a day.
 *
 * Recent items are shown before anything is typed, from `localStorage` — a
 * per-viewer convenience, not state anything else depends on.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import type { GlobalSearchHit } from '@menlo/shared';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

const RECENTS_KEY = 'menlo-funnel.recent-items';
const OPP_NUMBER = /^opp-\d{1,10}$/i;

interface Recent {
  label: string;
  href: string;
  kind: string;
}

function loadRecents(): Recent[] {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Recent[]).slice(0, 8) : [];
  } catch {
    return [];
  }
}

export function rememberRecent(item: Recent): void {
  try {
    const existing = loadRecents().filter((recent) => recent.href !== item.href);
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify([item, ...existing].slice(0, 8)));
  } catch {
    // Storage can be unavailable; a missing recents list is not an error.
  }
}

const KIND_LABEL: Record<string, string> = {
  opportunity: 'Opportunities',
  account: 'Accounts',
  contact: 'Contacts',
  program: 'Programs',
  note: 'Notes',
};

export function CommandPalette(): React.ReactElement | null {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<GlobalSearchHit[]>([]);
  const [highlight, setHighlight] = useState(0);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((current) => !current);
      }
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open || term.trim().length < 2) {
      setHits([]);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const result = await api<{ items: GlobalSearchHit[] }>('/api/search', {
          method: 'POST',
          body: { term: term.trim(), limit: 8 },
        });
        if (cancelled) return;
        setHits(result.items);
        setHighlight(0);

        // A bare opp number, and exactly one opportunity came back: go there.
        if (OPP_NUMBER.test(term.trim())) {
          const opportunities = result.items.filter((hit) => hit.kind === 'opportunity');
          if (opportunities.length === 1) {
            const hit = opportunities[0]!;
            rememberRecent({ label: hit.label, href: hit.href, kind: hit.kind });
            setOpen(false);
            setTerm('');
            navigate(hit.href);
          }
        }
      } catch {
        if (!cancelled) setHits([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 180);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [term, open, navigate]);

  const recents = useMemo(() => (open ? loadRecents() : []), [open]);
  const rows = term.trim().length < 2 ? recents.map(toHit) : hits;

  const grouped = useMemo(() => {
    const groups = new Map<string, GlobalSearchHit[]>();
    for (const hit of rows) {
      groups.set(hit.kind, [...(groups.get(hit.kind) ?? []), hit]);
    }
    return [...groups.entries()];
  }, [rows]);

  const flat = grouped.flatMap(([, items]) => items);

  if (!open) return null;

  const go = (hit: GlobalSearchHit): void => {
    rememberRecent({ label: hit.label, href: hit.href, kind: hit.kind });
    setOpen(false);
    setTerm('');
    navigate(hit.href);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-24">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className="w-full max-w-xl overflow-hidden rounded-lg border bg-card shadow-xl"
      >
        <div className="flex items-center gap-2 border-b px-3">
          <Search className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <input
            ref={inputRef}
            aria-label="Search opportunities, accounts, contacts and notes"
            className="h-11 flex-1 bg-transparent text-sm outline-none"
            placeholder="Search, or paste an opp number…"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setHighlight((index) => Math.min(index + 1, flat.length - 1));
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setHighlight((index) => Math.max(index - 1, 0));
              } else if (event.key === 'Enter') {
                const hit = flat[highlight];
                if (hit !== undefined) go(hit);
              }
            }}
          />
          <kbd className="rounded border px-1 text-[10px] text-muted-foreground">esc</kbd>
        </div>

        <div className="max-h-80 overflow-y-auto p-2">
          {term.trim().length < 2 && recents.length > 0 && (
            <p className="px-2 pb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
              Recent
            </p>
          )}
          {grouped.map(([kind, items]) => (
            <div key={kind}>
              {term.trim().length >= 2 && (
                <p className="px-2 pb-1 pt-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                  {KIND_LABEL[kind] ?? kind}
                </p>
              )}
              <ul>
                {items.map((hit) => {
                  const index = flat.indexOf(hit);
                  return (
                    <li key={`${hit.kind}-${hit.id}-${hit.href}`}>
                      <button
                        type="button"
                        onMouseEnter={() => setHighlight(index)}
                        onClick={() => go(hit)}
                        className={cn(
                          'flex w-full flex-col items-start rounded px-2 py-1.5 text-left text-sm',
                          index === highlight && 'bg-accent',
                        )}
                      >
                        <span className="truncate">{hit.label}</span>
                        {hit.sublabel !== null && (
                          <span className="truncate text-[11px] text-muted-foreground">
                            {hit.sublabel}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {term.trim().length >= 2 && !searching && rows.length === 0 && (
            <p className="px-2 py-6 text-center text-xs text-muted-foreground">Nothing found.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function toHit(recent: Recent): GlobalSearchHit {
  return {
    kind: recent.kind as GlobalSearchHit['kind'],
    id: recent.href,
    label: recent.label,
    sublabel: null,
    href: recent.href,
  };
}
